// pages/FreelanceDashboard.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/auth';
import { schedule as scheduleApi, bookings as bookingsApi, sessions as sessionsApi } from '../utils/api';
import api from '../utils/api';
import { format, startOfWeek, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, Clock, Calendar, Timer, FileText,
         ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';

const safeFormat = (val, fmt, opts={}) => {
  if (!val) return '—';
  try { const d=new Date(val); if(isNaN(d.getTime())) return '—'; return format(d,fmt,opts); }
  catch { return '—'; }
};

// ── Statut pointage + timer ───────────────────────────────────
function ClockWidget({ userId }) {
  const [isClockedIn, setIn] = useState(false);
  const [entry, setEntry]    = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const load = () => {
    scheduleApi.clockStatus()
      .then(r => { setIn(r.data.isClockedIn); setEntry(r.data.entry); })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!isClockedIn || !entry?.clock_in) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now()-new Date(entry.clock_in).getTime())/1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [isClockedIn, entry?.clock_in]);

  const clockIn = async () => {
    try {
      const r = await scheduleApi.clockIn();
      setIn(true); setEntry(r.data.entry);
      toast.success('Pointage entrée enregistré');
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const clockOut = async () => {
    if (!entry) return;
    try {
      await scheduleApi.clockOut(entry.id);
      setIn(false); setEntry(null); setElapsed(0);
      toast.success('Pointage sortie enregistré');
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const hh = String(Math.floor(elapsed/3600)).padStart(2,'0');
  const mm = String(Math.floor((elapsed%3600)/60)).padStart(2,'0');
  const ss = String(elapsed%60).padStart(2,'0');

  return (
    <div className="card" style={{
      borderColor: isClockedIn ? 'var(--teal)' : 'var(--border)',
      background: isClockedIn ? 'rgba(0,210,180,0.04)' : 'var(--bg-card)',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{background: isClockedIn?'rgba(0,210,180,0.15)':'var(--bg-elevated)',
          padding:14, borderRadius:12}}>
          <Clock size={22} color={isClockedIn?'var(--teal)':'var(--text-3)'}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:14,color:isClockedIn?'var(--teal)':'var(--text-2)'}}>
            {isClockedIn ? 'En service' : 'Hors service'}
          </div>
          {isClockedIn && entry && (
            <div style={{fontSize:12,color:'var(--text-3)',marginTop:2}}>
              Depuis {safeFormat(entry.clock_in,'HH:mm')}
            </div>
          )}
        </div>
        {isClockedIn && (
          <div style={{fontFamily:'monospace',fontSize:26,fontWeight:800,color:'var(--teal)'}}>
            {hh}:{mm}:{ss}
          </div>
        )}
        {isClockedIn
          ? <button className="btn btn-danger" onClick={clockOut}><LogOut size={14}/> Pointer la sortie</button>
          : <button className="btn btn-teal" onClick={clockIn}><LogIn size={14}/> Pointer l'entrée</button>
        }
      </div>
    </div>
  );
}

// ── Sessions du jour ──────────────────────────────────────────
function TodaySessions({ userId }) {
  const [sessions, setSessions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const today = format(new Date(),'yyyy-MM-dd');
    api.get('/bookings', {params:{gmUserId:userId, date:today}})
      .then(r => setSessions(r.data.bookings||[]))
      .catch(() => {});
  }, [userId]);

  if (!sessions.length) return (
    <div className="card">
      <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>Sessions aujourd'hui</div>
      <div style={{color:'var(--text-3)',fontSize:13,textAlign:'center',padding:'16px 0'}}>
        Aucune session assignée aujourd'hui
      </div>
    </div>
  );

  return (
    <div className="card">
      <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>
        Sessions aujourd'hui — {sessions.length} session{sessions.length>1?'s':''}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {sessions.map(s => (
          <div key={s.id} onClick={() => navigate(`/app/reservations/${s.id}`)}
            style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',
              borderRadius:10,cursor:'pointer',background:'var(--bg-elevated)',
              border:'1px solid var(--border)',transition:'all 0.1s'}}
            onMouseEnter={e=>e.currentTarget.style.borderColor='var(--accent)'}
            onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
            <div style={{fontFamily:'monospace',fontSize:16,fontWeight:700,
              color:'var(--accent-light)',flexShrink:0}}>
              {s.session_start?.slice(0,5)}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:600,fontSize:13}}>{s.room_name}</div>
              <div style={{fontSize:12,color:'var(--text-3)'}}>
                {s.player_count} joueur{s.player_count>1?'s':''} · {s.customer_name}
              </div>
            </div>
            <span className={`badge ${s.payment_status==='paid'?'badge-green':s.payment_status==='partial'?'badge-amber':'badge-red'}`}
              style={{fontSize:10}}>
              {s.payment_status==='paid'?'Soldé':s.payment_status==='partial'?'Acompte':'Impayé'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Planning semaine perso ────────────────────────────────────
function MyWeekSchedule({ userId }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(),{weekStartsOn:1}));
  const [schedules, setSchedules] = useState([]);
  const weekDays = Array.from({length:7},(_,i)=>addDays(weekStart,i));

  useEffect(() => {
    scheduleApi.list({week: format(weekStart,'yyyy-MM-dd'), userId})
      .then(r => setSchedules(r.data.schedules||[]))
      .catch(() => {});
  }, [weekStart, userId]);

  const totalMin = schedules.reduce((sum,s) => {
    if (!s.start_time||!s.end_time) return sum;
    const [sh,sm]=s.start_time.slice(0,5).split(':').map(Number);
    const [eh,em]=s.end_time.slice(0,5).split(':').map(Number);
    let m=(eh*60+em)-(sh*60+sm); if(m<0) m+=24*60;
    return sum+m;
  }, 0);

  return (
    <div className="card">
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{fontWeight:700,fontSize:14}}>Mon planning</div>
        <button className="btn btn-secondary btn-sm" onClick={()=>setWeekStart(d=>addDays(d,-7))}>
          <ChevronLeft size={14}/>
        </button>
        <span style={{fontSize:13,fontWeight:600}}>
          {format(weekStart,'d MMM',{locale:fr})} – {format(addDays(weekStart,6),'d MMM',{locale:fr})}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={()=>setWeekStart(d=>addDays(d,7))}>
          <ChevronRight size={14}/>
        </button>
        <button className="btn btn-ghost btn-sm" onClick={()=>setWeekStart(startOfWeek(new Date(),{weekStartsOn:1}))}>
          Cette semaine
        </button>
        {totalMin > 0 && (
          <span style={{marginLeft:'auto',fontSize:13,fontWeight:700,color:'var(--teal)'}}>
            {Math.floor(totalMin/60)}h{String(totalMin%60).padStart(2,'0')} cette semaine
          </span>
        )}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
        {weekDays.map(d => {
          const ds = format(d,'yyyy-MM-dd');
          const isToday = ds === format(new Date(),'yyyy-MM-dd');
          const sch = schedules.filter(s=>(s.work_date_str||s.work_date?.slice(0,10))===ds);
          return (
            <div key={ds} style={{textAlign:'center'}}>
              <div style={{fontSize:11,color:isToday?'var(--accent-light)':'var(--text-3)',
                fontWeight:isToday?700:400,marginBottom:4}}>
                {format(d,'EEE',{locale:fr})}
                <div style={{fontSize:12,fontWeight:isToday?700:400}}>{format(d,'d')}</div>
              </div>
              {sch.length > 0 ? sch.map(s => (
                <div key={s.id} style={{fontSize:10,padding:'3px 4px',borderRadius:6,marginBottom:2,
                  background: isToday?'rgba(124,92,252,0.2)':'rgba(124,92,252,0.1)',
                  color:'var(--accent-light)',fontWeight:600}}>
                  {s.start_time?.slice(0,5)}<br/>{s.end_time?.slice(0,5)}
                </div>
              )) : (
                <div style={{fontSize:11,color:'var(--text-3)',padding:'4px 0'}}>—</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Disponibilités freelance ──────────────────────────────────
function MyAvailability({ userId }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(),{weekStartsOn:1}));
  const [avail, setAvail]         = useState([]);
  const weekDays = Array.from({length:7},(_,i)=>addDays(weekStart,i));

  const load = () => {
    scheduleApi.availability({month: format(weekStart,'yyyy-MM')})
      .then(r => setAvail(r.data.availabilities||[]))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [weekStart]);

  const toggle = async (date, current) => {
    try {
      await scheduleApi.setAvailability({availDate:date, isAvailable:!current});
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="card">
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{fontWeight:700,fontSize:14}}>Mes disponibilités</div>
        <button className="btn btn-secondary btn-sm" onClick={()=>setWeekStart(d=>addDays(d,-7))}><ChevronLeft size={14}/></button>
        <span style={{fontSize:13,fontWeight:600}}>
          {format(weekStart,'d MMM',{locale:fr})} – {format(addDays(weekStart,6),'d MMM',{locale:fr})}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={()=>setWeekStart(d=>addDays(d,7))}><ChevronRight size={14}/></button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:6}}>
        {weekDays.map(d => {
          const ds = format(d,'yyyy-MM-dd');
          const isToday = ds===format(new Date(),'yyyy-MM-dd');
          const a = avail.find(av=>(av.avail_date_str||av.avail_date?.slice(0,10))===ds);
          const isDispo = a?.is_available;
          return (
            <div key={ds} onClick={() => toggle(ds, isDispo)}
              style={{textAlign:'center',padding:'8px 4px',borderRadius:10,cursor:'pointer',
                background: isDispo?'rgba(0,210,180,0.12)':'var(--bg-elevated)',
                border:`1px solid ${isDispo?'var(--teal)':isToday?'var(--accent)':'var(--border)'}`,
                transition:'all 0.15s'}}>
              <div style={{fontSize:11,color:isToday?'var(--accent-light)':'var(--text-3)',fontWeight:isToday?700:400}}>
                {format(d,'EEE',{locale:fr})}
              </div>
              <div style={{fontSize:12,fontWeight:600,marginTop:2,color:isDispo?'var(--teal)':'var(--text-3)'}}>
                {format(d,'d')}
              </div>
              <div style={{fontSize:10,marginTop:4,color:isDispo?'var(--teal)':'var(--text-3)'}}>
                {isDispo ? '✓ Dispo' : '—'}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{fontSize:11,color:'var(--text-3)',marginTop:8,textAlign:'center'}}>
        Cliquez sur un jour pour indiquer votre disponibilité
      </div>
    </div>
  );
}

// ── Missions récentes ─────────────────────────────────────────
function RecentMissions({ userId }) {
  const [missions, setMissions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const month = format(new Date(),'yyyy-MM');
    api.get('/bookings', {params:{gmUserId:userId, month}})
      .then(r => setMissions((r.data.bookings||[]).slice(0,8)))
      .catch(() => {});
  }, [userId]);

  const exportCSV = () => {
    const header = 'Date,Heure,Salle,Joueurs,Référence,Paiement';
    const rows = missions.map(m=>[
      m.session_date?.slice(0,10),
      m.session_start?.slice(0,5),
      m.room_name,
      m.player_count,
      m.booking_ref,
      m.payment_status,
    ].join(','));
    const csv=[header,...rows].join('\n');
    const blob=new Blob([csv],{type:'text/csv'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url; a.download=`missions-${format(new Date(),'yyyy-MM')}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card">
      <div style={{display:'flex',alignItems:'center',marginBottom:12}}>
        <div style={{fontWeight:700,fontSize:14}}>Missions ce mois</div>
        {missions.length > 0 && (
          <button className="btn btn-secondary btn-sm" style={{marginLeft:'auto',fontSize:11}}
            onClick={exportCSV}>
            ↓ Export facturation
          </button>
        )}
      </div>
      {missions.length === 0 ? (
        <div style={{color:'var(--text-3)',fontSize:13,textAlign:'center',padding:'12px 0'}}>
          Aucune mission ce mois
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {missions.map(m => (
            <div key={m.id} onClick={() => navigate(`/app/reservations/${m.id}`)}
              style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',
                borderRadius:8,cursor:'pointer',background:'var(--bg-elevated)',
                border:'1px solid var(--border)',fontSize:13,transition:'all 0.1s'}}
              onMouseEnter={e=>e.currentTarget.style.borderColor='var(--accent)'}
              onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
              <span style={{color:'var(--text-3)',fontSize:12,minWidth:70}}>
                {safeFormat(m.session_date?.slice(0,10)+'T12:00:00','d MMM',{locale:fr})} {m.session_start?.slice(0,5)}
              </span>
              <span style={{flex:1,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {m.room_name}
              </span>
              <span style={{color:'var(--text-3)',fontSize:12}}>{m.player_count}p</span>
              <span className={`badge ${m.payment_status==='paid'?'badge-green':m.payment_status==='partial'?'badge-amber':'badge-red'}`}
                style={{fontSize:10}}>
                {m.payment_status==='paid'?'Soldé':m.payment_status==='partial'?'Acompte':'Impayé'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Mes heures du mois ────────────────────────────────────────
function MyMonthHours({ userId }) {
  const [entries, setEntries] = useState([]);
  const month = format(new Date(),'yyyy-MM');

  useEffect(() => {
    scheduleApi.myTime({month})
      .then(r => setEntries(r.data.entries||[]))
      .catch(() => {});
  }, []);

  const totalMin = entries.filter(e=>e.clock_out).reduce((s,e)=>s+(parseInt(e.duration_min)||0),0);
  const jours    = new Set(entries.filter(e=>e.clock_out).map(e=>e.work_date?.slice(0,10))).size;
  const openEntry = entries.find(e=>!e.clock_out);

  return (
    <div className="card">
      <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>
        Mes heures — {format(new Date(month+'-15'),'MMMM yyyy',{locale:fr})}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
        {[
          ['Heures effectuées', `${Math.floor(totalMin/60)}h${String(totalMin%60).padStart(2,'0')}`, 'var(--teal)'],
          ['Jours travaillés', jours, 'var(--accent-light)'],
          ['En cours', openEntry ? '⏱ Oui' : '—', openEntry?'var(--amber)':'var(--text-3)'],
        ].map(([label,val,color])=>(
          <div key={label} style={{textAlign:'center',padding:'10px 8px',borderRadius:10,
            background:'var(--bg-elevated)'}}>
            <div style={{fontSize:11,color:'var(--text-3)',marginBottom:4}}>{label}</div>
            <div style={{fontSize:20,fontWeight:800,color}}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Dashboard principal Freelance ─────────────────────────────
export default function FreelanceDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Bonjour, {user?.first_name} 👋</h2>
          <p>{format(new Date(),'EEEE d MMMM yyyy',{locale:fr})}</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={()=>navigate('/app/planning')}>
            <Calendar size={14}/> Planning RH
          </button>
          <button className="btn btn-secondary" onClick={()=>navigate('/app/timer')}>
            <Timer size={14}/> Timer
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* Pointage */}
        <ClockWidget userId={user?.id}/>

        {/* Sessions du jour + Mes heures */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginTop:16}}>
          <TodaySessions userId={user?.id}/>
          <MyMonthHours userId={user?.id}/>
        </div>

        {/* Planning semaine + Disponibilités */}
        <div style={{marginTop:16}}>
          <MyWeekSchedule userId={user?.id}/>
        </div>
        <div style={{marginTop:16}}>
          <MyAvailability userId={user?.id}/>
        </div>

        {/* Missions du mois */}
        <div style={{marginTop:16}}>
          <RecentMissions userId={user?.id}/>
        </div>
      </div>
    </>
  );
}
